import {
    OfflineData,
    OfflineDataManager,
    MutexLock,
    MultiTabLock,
    ClientMutexLock,
    BaseBusinessHelper,
} from '@tencent/docs_offline';
import ua from '@tencent/docs-user-agent';
import { LockItem } from '@tencent/docs_offline/lib/tools/MutexLock';
import { QueryCondition, BaseDatabase, BaseError, Result } from '@tencent/tdoc-database';
import { DOCUMENT_TYPE } from '@tencent/ark-model';
import { Reporter007 } from '@src/slide/infrastructure/module/dependent/report/real-time-report/constant';
import { ILogger } from '../../../core/slide-data-services.core';
import { OnlineEditServices } from '../../online/core/online-edit-services.core';

export enum IndexDBStatus {
    InitUnlaunch = 0,
    Initing = 1,
    InitException = 2,   // 初始化完成，结果：异常
    InitFinish = 3,      // 初始化完成，结果：成功
    InitTimeout = 4,     // 初始化超时了
}

export enum OperationType {
    set = 'set',
    update = 'update',
    updateField = 'updateField',
    delete = 'delete',
    get = 'get',
    getAll = 'getAll',
}

export interface IndexDBOperate {
    type: OperationType;
    tableName: string;
    data?: any;
    where?: QueryCondition;
}

const win = (window as any);

const MODULE_NAME = '[DBH]';

export default class DBHelper {
    public dataManager: OfflineDataManager;
    public mutexTabLockReason: any = undefined;
    public DBstatus: IndexDBStatus = IndexDBStatus.InitUnlaunch;
    public DBStatusPromise: Promise<IndexDBStatus>;

    private mutexTabLock: MutexLock;

    public constructor(private logger: ILogger) {
        this.logger.info(MODULE_NAME, 'constructor');

        this.dataManager = new OfflineDataManager({
            businessType: DOCUMENT_TYPE.slide as any,
        });

        if (ua.isSupportNativeDatabase) {
            this.mutexTabLock = new ClientMutexLock();
        } else {
            this.mutexTabLock = new MultiTabLock();
        }

        this.dataManageInit();
    }

    public getUnCommittedOfflineData() {
        return this.promiseWithTimeout<Result<OfflineData[] | undefined>>((resolve, reject) => {
            const rejectCallback = (errMsg: string) => {
                this.logger.error(MODULE_NAME, 'getUnCommittedOfflineData error:', errMsg);
                reject(errMsg);
            };
            const slideHelper = this.getSlideHelper();
            if (!slideHelper) {
                rejectCallback('slideHelper is undefined');
                return;
            }
            slideHelper.getUncommitedOfflineData()
                .then(resolve)
                .catch(err => rejectCallback(err?.toString()));
            return rejectCallback;
        }, 10000);
    }

    /**
     * 锁
     * @param secretId secretId
     */
    public lock(secretId: string) {
        return this.promiseWithTimeout<{
            isSuccess: boolean;
            data: LockItem;
        }>((resolve, reject) => {
            const rejectCallback = (errMsg: string) => {
                this.logger.error(MODULE_NAME, 'lock error:', errMsg);
                reject(errMsg);
            };
            this.mutexTabLock.lock(`offline_${secretId}`, { source: 'edit' })
                .then(resolve)
                .catch(err => rejectCallback(err?.toString()));
            return rejectCallback;
        });
    }

    /**
     * 释放锁
     * @param secretId secretId
     */
    public release(secretId: string) {
        return this.promiseWithTimeout<boolean>((resolve, reject) => {
            const rejectCallback = (errMsg: string) => {
                this.logger.error(MODULE_NAME, 'release error:', errMsg);
                reject(errMsg);
            };
            this.mutexTabLock.release(`offline_${secretId}`)
                .then(resolve)
                .catch(err => rejectCallback(err?.toString()));
            return rejectCallback;
        });
    }

    public disConnect() {
        return this.promiseWithTimeout<Result<boolean>>((resolve, reject) => {
            const rejectCallback = (errMsg: string) => {
                this.logger.error(MODULE_NAME, 'disConnect error:', errMsg);
                reject(errMsg);
            };
            const database = this.getDataBase();
            if (!database) {
                rejectCallback('database is undefined');
                return;
            }
            database.disConnect()
                .then(resolve)
                .catch(err => rejectCallback(err?.toString()));
            return rejectCallback;
        });
    }

    public async handleDataFromIndexDB<T extends OperationType>(op: IndexDBOperate) {
        const firstResult = this.opCheckFirst(op);
        if (firstResult.error) return firstResult;
        const secondResult = this.opCheckSecond(op);
        if (secondResult.error) return secondResult;
        const database = this.getDataBase();
        if (!database) throw new Error('database is undefined');
        const data = await this.handleOpWithTimeout<T>(database, op);
        return data;
    }

    private dataManageInit() {
        const indexDadaBaseStart = +new Date();
        this.DBstatus = IndexDBStatus.Initing;
        this.DBStatusPromise = this.promiseWithTimeout<IndexDBStatus>((resolve, reject) => {
            const rejectCallback = (errMsg: string) => {
                this.DBstatus = IndexDBStatus.InitException;
                this.logger.error(MODULE_NAME, 'dataManageInit error:', errMsg);
                reject(errMsg);
            };
            this.dataManager.init()
                .then((indexDBInitResult: Result<boolean>) => {
                    if (indexDBInitResult.error) {
                        rejectCallback(indexDBInitResult.error.message);
                        return;
                    }
                    this.DBstatus = IndexDBStatus.InitFinish;
                    this.logger.info(MODULE_NAME, `dataManageInit cost ${+new Date() - indexDadaBaseStart}ms status: success`);
                    resolve(this.DBstatus);
                })
                .catch((err) => {
                    rejectCallback(err?.toString());
                });
            return rejectCallback;
        }, 10000);
    }

    private handleOpWithTimeout<T extends OperationType>(
        database: BaseDatabase,
        op: IndexDBOperate,
    ): Promise<ReturnType<BaseDatabase[T]> | Result<undefined>> {
        return this.promiseWithTimeout<ReturnType<BaseDatabase[T]> | Result<undefined>>((resolve, reject) => {
            const rejectCallback = (errMsg: string) => {
                this.logger.error(MODULE_NAME, 'DB Operation', op.type, errMsg);
                reject(errMsg);
            };
            this.handleOp<T>(database, op)
                .then(resolve)
                .catch((err) => {
                    rejectCallback(err?.toString());
                });
            return rejectCallback;
        });
    }

    // eslint-disable-next-line complexity
    private async handleOp<T extends OperationType>(
        database: BaseDatabase,
        op: IndexDBOperate,
    ): Promise<ReturnType<BaseDatabase[T]> | Result<undefined>> {
        const result = new Result(undefined, undefined);
        switch (op.type) {
            case OperationType.set:
                return database.set(op.tableName, op.data) as ReturnType<BaseDatabase[T]>;
            case OperationType.update:
                return database.update(op.tableName, op.data) as ReturnType<BaseDatabase[T]>;
            case OperationType.updateField:
                if (op.where) {
                    return database.updateField(op.tableName, op.where, op.data) as ReturnType<BaseDatabase[T]>;
                }
                break;
            case OperationType.delete:
                if (op.where) {
                    return database.delete(op.tableName, op.where) as ReturnType<BaseDatabase[T]>;
                }
                break;
            case OperationType.get:
                if (op.where) {
                    return database.get(op.tableName, op.where) as ReturnType<BaseDatabase[T]>;
                }
                break;
            case OperationType.getAll:
                if (op.where) {
                    return database.getAll(op.tableName, op.where) as ReturnType<BaseDatabase[T]>;
                } break;
            default:
                break;
        }
        return result;
    }


    private getDataBase(): BaseDatabase | undefined {
        return this.dataManager.getDatabase();
    }

    private getSlideHelper(): BaseBusinessHelper | undefined {
        return this.dataManager.getHelper(DOCUMENT_TYPE.slide as any);
    }

    private promiseWithTimeout<T>(
        action: (
            resolve: (data: T) => void,
            reject: (errMsg: string) => void) => undefined | ((errMsg: string) => void),
        timeout = 5000,
    ) {
        return new Promise<T>((resolve, reject) => {
            const handler = { reject: (errMsg: string) => {} };
            // 超时错误不能直接调用 reject，要调用上下文中的 reject
            const timer = setTimeout(() => handler.reject('timeout'), timeout);
            const rejectCallback = (errMsg: string) => {
                clearTimeout(timer);
                this.dispatchDBError(errMsg);
                this.logger.monitor?.reportStatus?.('DBHelper', Reporter007.ERROR, errMsg);
                reject(errMsg);
            };
            const resolveCallback = (data: T) => {
                clearTimeout(timer);
                resolve(data);
            };
            handler.reject = action(resolveCallback, rejectCallback) ?? rejectCallback;
        });
    }

    private opCheckFirst = (op: IndexDBOperate) => {
        const result = new Result(undefined, undefined);
        if (!op.type || !op.tableName) {
            const reason = 'oprate or tableName is undefine.';
            win.log.error(`${reason}`);
            result.error = new BaseError(reason);
        }
        return result;
    };

    private opCheckSecond = (op: IndexDBOperate) => {
        const result = new Result(undefined, undefined);
        if ((op.type === OperationType.set || op.type === OperationType.update || op.type === OperationType.delete)
            && this.mutexTabLockReason) {
            const reason = 'has not mutexLock, can not write indexDB ';
            this.logger.error(`${reason}`);
            result.error = new BaseError(reason);
        }
        return result;
    };

    private async dispatchDBError(errMsg: string) {
        const persistent = (await OnlineEditServices.CollaborationManager.promise())?.getPersistent?.();
        (persistent as any)?.handleDatabaseError?.(errMsg, 'desktop', true);
    }
}
